import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { invoiceApi, seminarApi, expenseApi, cancellationsApi } from '../../lib/api';
import { formatCurrency, downloadBlob } from '../../lib/utils';
import PaymentDialog from '../dialogs/PaymentDialog';
import { CollectiveInvoiceActions } from '../common/CollectiveInvoiceActions';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
    Download, CheckCircle, Loader2, Search, Receipt, Euro, XCircle,
    Plus, Trash2, FileText, Upload, Check, Printer, TrendingUp, TrendingDown, Minus,
    PieChart, Banknote, ArrowDownRight, ArrowUpRight, Pencil, Users, RotateCcw
} from 'lucide-react';

function fmtDate(d) {
    if (!d) return '';
    try {
        if (d.includes('T')) d = d.split('T')[0];
        if (d.includes('-')) { const [y, m, day] = d.split('-'); return `${day}.${m}.${y}`; }
    } catch { /* */ }
    return d;
}

const EXPENSE_CATEGORIES = {
    referentenhonorar: 'Referentenhonorar',
    raummiete: 'Raummiete',
    material: 'Materialkosten',
    catering: 'Verpflegung',
    reisekosten: 'Reisekosten',
    sonstige: 'Sonstige Ausgaben',
};

// Extrahiert das Jahr aus einem Seminar – primär aus der Kursnummer (z. B. "BGPR-2026-01"),
// Fallback auf den ersten Termin oder das Erstellungsdatum.
function getSeminarYear(s) {
    if (!s) return null;
    const cn = s.course_number || '';
    const m = cn.match(/(\d{4})/);
    if (m) return parseInt(m[1], 10);
    const firstDate = s.dates?.[0]?.date;
    if (firstDate) {
        const yr = parseInt(firstDate.slice(0, 4), 10);
        if (!Number.isNaN(yr)) return yr;
    }
    if (s.created_at) {
        const yr = parseInt(String(s.created_at).slice(0, 4), 10);
        if (!Number.isNaN(yr)) return yr;
    }
    return null;
}

export function InvoicesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Tab via URL persistieren, damit Browser-Back vom Edit-Formular auf den
    // ursprünglichen Tab zurückkehrt (statt 'overview' zu zeigen).
    const validTabs = ['overview', 'income', 'expenses'];
    const urlTab = searchParams.get('tab');
    const [activeTab, setActiveTabState] = useState(validTabs.includes(urlTab) ? urlTab : 'overview');
    const setActiveTab = (v) => {
        setActiveTabState(v);
        const next = new URLSearchParams(searchParams);
        if (v === 'overview') next.delete('tab'); else next.set('tab', v);
        setSearchParams(next, { replace: false });
    };
    const [showCompare, setShowCompare] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [seminars, setSeminars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seminarFilter, setSeminarFilter] = useState('all');
    const [yearFilter, setYearFilter] = useState(() => String(new Date().getFullYear()));
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Dialogs
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [paymentData, setPaymentData] = useState({ paid_amount: 0, payment_date: '', payment_method: 'ueberweisung', payment_reference: '' });
    const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
    const [expenseForm, setExpenseForm] = useState({ seminar_id: '', category: 'referentenhonorar', description: '', amount: 0, recipient: '' });
    const [expPaidDialog, setExpPaidDialog] = useState(null);
    const [expPaidDate, setExpPaidDate] = useState('');
    const [refundDialog, setRefundDialog] = useState(null);
    const [refundForm, setRefundForm] = useState({ refund_date: '', refund_reference: '' });
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const fileRef = useRef(null);
    const [uploadingId, setUploadingId] = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [inv, sem, exp] = await Promise.all([invoiceApi.getAll(), seminarApi.getAll(), expenseApi.getAll()]);
            setInvoices(inv.data); setSeminars(sem.data); setExpenses(exp.data);
        } catch { toast.error('Fehler beim Laden'); }
        finally { setLoading(false); }
    };

    const getSeminarName = (id) => seminars.find(s => s.id === id)?.title || '—';

    // Verfügbare Jahre für den Jahres-Filter, immer absteigend sortiert.
    const availableYears = React.useMemo(() => {
        const set = new Set();
        seminars.forEach(s => { const y = getSeminarYear(s); if (y) set.add(y); });
        set.add(new Date().getFullYear());
        return Array.from(set).sort((a, b) => b - a);
    }, [seminars]);

    // Seminare im aktuellen Jahr-Filter (für das Seminar-Dropdown).
    const seminarsInYear = React.useMemo(() => {
        if (yearFilter === 'all') return seminars;
        const y = parseInt(yearFilter, 10);
        return seminars.filter(s => getSeminarYear(s) === y);
    }, [seminars, yearFilter]);

    // Falls das aktuell gewählte Seminar nicht ins Jahr passt → Auswahl auf "alle" zurücksetzen.
    useEffect(() => {
        if (seminarFilter !== 'all' && !seminarsInYear.some(s => s.id === seminarFilter)) {
            setSeminarFilter('all');
        }
    }, [seminarsInYear, seminarFilter]);

    // Income handlers
    const handleMarkPaid = async () => {
        if (!paymentData.payment_date || !paymentData.paid_amount) { toast.error('Bitte Datum und Betrag eingeben'); return; }
        setSaving(true);
        try { await invoiceApi.markPaid(selectedInvoice.id, paymentData); toast.success('Zahlungseingang erfasst'); setPayDialogOpen(false); loadData(); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };
    const handleDownloadPdf = async (inv) => {
        setDownloading(inv.id);
        try { const r = await invoiceApi.downloadPdf(inv.id); downloadBlob(r.data, `Quittung_${inv.receipt_number || inv.id.slice(0, 8)}.pdf`); }
        catch { toast.error('Fehler'); }
        finally { setDownloading(null); }
    };

    // Fehlbuchung zurücknehmen: entfernt den letzten Zahlungseintrag, gibt bei
    // vollständig bezahlten Rechnungen die Quittungsnummer frei und löscht die
    // Quittung aus Dropbox + Portal.
    const handleRevertPayment = async (inv) => {
        const payments = inv.payments || [];
        if (payments.length === 0) { toast.error('Keine Zahlung vorhanden'); return; }
        // Neuesten Eintrag zurücknehmen (typischer Anwendungsfall: gerade versehentlich erfasst)
        const sorted = [...payments].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        const latest = sorted[0];
        const hasMore = payments.length > 1;
        const receiptHint = inv.status === 'paid' && !inv.is_in_house_collective
            ? '\n\n⚠️ Die Quittung wird zurückgezogen: Nummer entfernt, Portal-Download nicht mehr abrufbar, Dropbox-Datei wird gelöscht.'
            : '';
        const multiHint = hasMore
            ? `\n\nHinweis: Diese Rechnung hat ${payments.length} Zahlungen. Es wird die zuletzt erfasste zurückgenommen. Weitere ggf. einzeln in der Teilnehmer-Detailansicht rücknehmen.`
            : '';
        const confirmed = window.confirm(
            `Zahlungseingang zurücknehmen?\n\n`
            + `${inv.is_in_house_collective ? "Firma" : "Teilnehmer"}: ${inv.participant_name || '—'}\n`
            + `Betrag: ${formatCurrency(latest.amount)}\n`
            + `Datum: ${fmtDate(latest.date)}\n`
            + (latest.reference ? `Referenz: ${latest.reference}\n` : '')
            + receiptHint
            + multiHint
            + `\n\nDiese Aktion wird protokolliert.`
        );
        if (!confirmed) return;
        const reason = window.prompt('Grund für die Rücknahme (optional, für Audit-Log):', '') || '';
        try {
            const res = await invoiceApi.revertPayment(inv.id, latest.id, reason);
            const d = res.data || {};
            const parts = [];
            if (d.receipt_removed) parts.push('Quittungsnummer entfernt');
            if (d.dropbox_receipt_deleted === true) parts.push('Dropbox-Quittung gelöscht');
            else if (d.dropbox_receipt_deleted === false) parts.push('⚠ Dropbox-Löschung fehlgeschlagen');
            toast.success(`Zahlung zurückgenommen${parts.length ? ' · ' + parts.join(' · ') : ''}`);
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rücknahme fehlgeschlagen');
        }
    };

    // Buchhaltungs-PDF-Exporte (Gesamtübersicht, Einnahmen, Ausgaben, Offene, Rückerstattungen, Kostenfreie)
    const exportPdf = async (type) => {
        const labels = {
            overview: 'Gesamtübersicht', income: 'Einnahmen', expenses: 'Ausgaben',
            open: 'Offene-Forderungen', refunds: 'Rueckerstattungen', free: 'Kostenfreie-Teilnehmer',
        };
        try {
            const r = await invoiceApi.exportAccounting(type, yearFilter, seminarFilter);
            const yr = yearFilter === 'all' ? 'alle' : yearFilter;
            downloadBlob(r.data, `Buchhaltung_${labels[type]}_${yr}.pdf`);
            toast.success(`${labels[type]} als PDF exportiert`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Export fehlgeschlagen');
        }
    };

    // One-shot: bereits in Dropbox abgelegte Quittungen mit aktueller
    // PDF-Engine neu rendern und überschreiben (z.B. nach Render-Fix für
    // base64-Hintergrundbilder). Idempotent; verschickt keine E-Mails.
    const [resyncing, setResyncing] = useState(false);
    const [resyncDialogOpen, setResyncDialogOpen] = useState(false);
    const [resyncSelectedIds, setResyncSelectedIds] = useState([]);
    const [resyncSearch, setResyncSearch] = useState('');

    const openResyncDialog = () => {
        setResyncSelectedIds([]);
        setResyncSearch('');
        setResyncDialogOpen(true);
    };

    const handleResyncReceipts = async () => {
        if (resyncSelectedIds.length === 0) {
            toast.error('Bitte mindestens eine Quittung auswählen');
            return;
        }
        setResyncing(true);
        try {
            const r = await invoiceApi.resyncReceiptsToDropbox(resyncSelectedIds);
            const d = r.data || {};
            const errCount = (d.errors || []).length;
            const skipCount = (d.skipped || []).length;
            if (errCount === 0) {
                toast.success(`${d.regenerated} Quittung(en) in Dropbox erneuert${skipCount ? ` · ${skipCount} übersprungen` : ''}`);
                setResyncDialogOpen(false);
            } else {
                toast.error(`${d.regenerated} erneuert, ${errCount} Fehler – siehe Backend-Log`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Resync fehlgeschlagen');
        } finally {
            setResyncing(false);
        }
    };

    // Expense handlers
    const handleCreateExpense = async () => {
        if (!expenseForm.description || !expenseForm.amount) { toast.error('Beschreibung und Betrag erforderlich'); return; }
        setSaving(true);
        try { await expenseApi.create(expenseForm); toast.success('Ausgabe erfasst'); setExpenseDialogOpen(false); setExpenseForm({ seminar_id: '', category: 'referentenhonorar', description: '', amount: 0, recipient: '' }); loadData(); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };
    const handleApproveExpense = async (id) => {
        try { await expenseApi.approve(id); toast.success('Freigegeben'); loadData(); } catch { toast.error('Fehler'); }
    };
    const handleToggleApprove = async (row) => {
        try {
            await expenseApi.approve(row.id, { approved: !row.approved });
            toast.success(row.approved ? 'Freigabe entfernt' : 'Freigegeben');
            loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler bei Freigabe');
        }
    };
    const handlePayExpense = async () => {
        if (!expPaidDate) { toast.error('Bitte Datum eingeben'); return; }
        setSaving(true);
        try { await expenseApi.markPaid(expPaidDialog.id, { paid_at: expPaidDate }); toast.success('Als bezahlt markiert'); setExpPaidDialog(null); loadData(); }
        catch { toast.error('Fehler'); }
        finally { setSaving(false); }
    };
    const handleDeleteExpense = async (id) => {
        if (!window.confirm('Ausgabe löschen?')) return;
        try { await expenseApi.delete(id); toast.success('Gelöscht'); loadData(); } catch { toast.error('Fehler'); }
    };
    const handleUploadExpenseDoc = async (id, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingId(id);
        try { await expenseApi.upload(id, file); toast.success('Beleg hochgeladen'); loadData(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
        finally { setUploadingId(null); if (fileRef.current) fileRef.current.value = ''; }
    };
    const handleDownloadAbrechnung = async () => {
        if (seminarFilter === 'all') { toast.error('Bitte zuerst ein Seminar auswählen'); return; }
        setDownloading('abrechnung');
        try { const r = await expenseApi.seminarAbrechnung(seminarFilter); const sem = seminars.find(s => s.id === seminarFilter); downloadBlob(r.data, `Abrechnung_${sem?.course_number || 'Seminar'}.pdf`); }
        catch { toast.error('Fehler'); }
        finally { setDownloading(null); }
    };

    // Set der Seminar-IDs des aktuellen Jahresfilters – wird für Auswertungen ohne
    // konkrete Seminar-Auswahl verwendet.
    const yearScopedSeminarIds = React.useMemo(() => {
        if (yearFilter === 'all') return null; // null = keine Jahreseinschränkung
        const ids = new Set();
        seminarsInYear.forEach(s => ids.add(s.id));
        return ids;
    }, [seminarsInYear, yearFilter]);

    // Filtered data
    const filteredInvoices = invoices.filter(i => {
        if (seminarFilter !== 'all') {
            if (i.seminar_id !== seminarFilter) return false;
        } else if (yearScopedSeminarIds) {
            if (!yearScopedSeminarIds.has(i.seminar_id)) return false;
        }
        if (statusFilter === 'refund') {
            // Nur Vorgänge mit Erstattungs-Information anzeigen (offen oder erstattet).
            if (!i.refund_status) return false;
        } else if (statusFilter !== 'all' && i.status !== statusFilter) {
            return false;
        }
        const q = searchTerm.toLowerCase();
        return (i.participant_name || '').toLowerCase().includes(q) || (i.receipt_number || '').toLowerCase().includes(q) || (i.invoice_number || '').toLowerCase().includes(q) || getSeminarName(i.seminar_id).toLowerCase().includes(q);
    });
    const filteredExpenses = expenses.filter(e => {
        if (seminarFilter !== 'all') {
            if (e.seminar_id !== seminarFilter) return false;
        } else if (yearFilter !== 'all') {
            // Ausgabe zum Jahr zählt, wenn entweder das verlinkte Seminar im Jahr liegt
            // ODER die Ausgabe selbst das Jahr trägt (z. B. seminarunabhängige Kosten).
            const yr = parseInt(yearFilter, 10);
            const seminarMatches = e.seminar_id && yearScopedSeminarIds && yearScopedSeminarIds.has(e.seminar_id);
            const dateMatches = e.expense_date && parseInt(String(e.expense_date).slice(0, 4), 10) === yr;
            if (!seminarMatches && !dateMatches) return false;
        }
        const q = searchTerm.toLowerCase();
        return (e.description || '').toLowerCase().includes(q) || (e.recipient || '').toLowerCase().includes(q);
    });

    // Erstattungen (Rückzahlungen aus Stornierungen) reduzieren die Einnahmen.
    // Sie werden NICHT als Ausgabe geführt – die Buchhaltung soll netto stimmen.
    const refundsPaid = filteredInvoices
        .filter(i => i.refund_status === 'paid')
        .reduce((s, i) => s + (i.refund_amount || 0), 0);
    const refundsOpen = filteredInvoices
        .filter(i => i.refund_status === 'pending')
        .reduce((s, i) => s + (i.refund_amount || 0), 0);
    const refundsOpenCount = filteredInvoices.filter(i => i.refund_status === 'pending').length;

    const grossIncome = filteredInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
    const totalIncome = grossIncome - refundsPaid;
    const totalOpen = filteredInvoices.filter(i => i.status === 'open' || i.status === 'partial').reduce((s, i) => s + (i.amount - (i.paid_amount || 0)), 0);
    const totalExpenses = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
    const expectedFees = filteredInvoices.reduce((s, i) => s + (i.amount || 0), 0);
    const paidCount = filteredInvoices.filter(i => i.status === 'paid').length;
    const balance = totalIncome - totalExpenses;

    // Ausgaben nach Kategorie für die Übersichts-Karte
    const expensesByCategory = React.useMemo(() => {
        const map = {};
        filteredExpenses.forEach(e => {
            const cat = e.category || 'sonstige';
            if (!map[cat]) map[cat] = { count: 0, sum: 0 };
            map[cat].count += 1;
            map[cat].sum += e.amount || 0;
        });
        return Object.entries(map)
            .map(([cat, v]) => ({ category: cat, label: EXPENSE_CATEGORIES[cat] || cat, ...v }))
            .sort((a, b) => b.sum - a.sum);
    }, [filteredExpenses]);

    const isSeminarScoped = seminarFilter !== 'all';
    const scopedSeminar = isSeminarScoped ? seminars.find(s => s.id === seminarFilter) : null;
    const tilesScopeLabel = scopedSeminar
        ? `Seminar ${scopedSeminar.course_number || ''} · ${scopedSeminar.title}`.trim()
        : (yearFilter === 'all' ? 'Alle Seminare (alle Jahre)' : `Alle Seminare ${yearFilter}`);

    // ===== Jahresvergleich =====
    // Nur sinnvoll, wenn ein konkretes Jahr (nicht 'all') und kein einzelnes Seminar gewählt ist.
    const showYearCompare = yearFilter !== 'all' && !isSeminarScoped;
    const yearCompare = React.useMemo(() => {
        if (!showYearCompare) return null;
        const currentYear = parseInt(yearFilter, 10);
        const prevYear = currentYear - 1;
        const prevYearSeminarIds = new Set(
            seminars.filter(s => getSeminarYear(s) === prevYear).map(s => s.id)
        );
        const sumIncome = (arr, sids) => {
            const gross = arr.filter(i => i.status === 'paid' && sids.has(i.seminar_id))
                .reduce((s, i) => s + (i.amount || 0), 0);
            const refunds = arr.filter(i => i.refund_status === 'paid' && sids.has(i.seminar_id))
                .reduce((s, i) => s + (i.refund_amount || 0), 0);
            return gross - refunds;
        };
        const sumExpenses = (arr, sids) => arr
            .filter(e => sids.has(e.seminar_id))
            .reduce((s, e) => s + (e.amount || 0), 0);
        const prevIncome = sumIncome(invoices, prevYearSeminarIds);
        const prevExpenses = sumExpenses(expenses, prevYearSeminarIds);
        const prevResult = prevIncome - prevExpenses;
        const curResult = totalIncome - totalExpenses;
        const pct = (cur, prev) => {
            if (!prev && !cur) return null;
            if (!prev) return null; // keine sinnvolle Prozentangabe ohne Vorjahresbasis
            return ((cur - prev) / Math.abs(prev)) * 100;
        };
        return {
            currentYear, prevYear,
            income: { cur: totalIncome, prev: prevIncome, pct: pct(totalIncome, prevIncome) },
            expenses: { cur: totalExpenses, prev: prevExpenses, pct: pct(totalExpenses, prevExpenses) },
            result: { cur: curResult, prev: prevResult, pct: pct(curResult, prevResult) },
            hasPrev: prevYearSeminarIds.size > 0 || prevIncome > 0 || prevExpenses > 0,
        };
    }, [showYearCompare, yearFilter, seminars, invoices, expenses, totalIncome, totalExpenses]);

    return (
        <AdminLayout>
            <PageHeader title="Buchhaltung" description="Einnahmen, Ausgaben und Seminarabrechnungen"
                actions={<div className="flex gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" data-testid="accounting-export-btn">
                                <Printer className="h-4 w-4 mr-1" /> Export PDF
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                            <DropdownMenuLabel className="text-xs text-muted-foreground">Buchhaltungs-Exporte</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => exportPdf('overview')} className="bg-sky-50 focus:bg-sky-100" data-testid="export-overview">
                                <Printer className="h-4 w-4 mr-2 text-sky-600" /> Gesamtübersicht
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => exportPdf('income')} data-testid="export-income">
                                <ArrowUpRight className="h-4 w-4 mr-2 text-emerald-600" /> Nur Einnahmen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => exportPdf('expenses')} data-testid="export-expenses">
                                <ArrowDownRight className="h-4 w-4 mr-2 text-rose-600" /> Nur Ausgaben
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => exportPdf('open')} data-testid="export-open">
                                <Euro className="h-4 w-4 mr-2 text-amber-600" /> <span className="text-amber-700">Offene Forderungen</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => exportPdf('refunds')} data-testid="export-refunds">
                                <Banknote className="h-4 w-4 mr-2 text-rose-600" /> <span className="text-rose-700">Rückerstattungen (Storno)</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => exportPdf('free')} data-testid="export-free">
                                <Users className="h-4 w-4 mr-2 text-teal-600" /> <span className="text-teal-700">Kostenfreie Teilnehmer</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={openResyncDialog}
                        data-testid="resync-receipts-dropbox-btn"
                        title="Bezahlte Quittungen auswählen und in Dropbox überschreiben"
                    >
                        Quittungen in Dropbox erneuern
                    </Button>
                </div>}
            />

            {/* Auswertungs-Scope Hinweis */}
            <div className="mb-3 text-xs text-muted-foreground" data-testid="accounting-scope-label">
                Auswertung: <span className="font-medium text-foreground">{tilesScopeLabel}</span>
            </div>

            {/* Top-Kacheln im Eventbuchhaltungs-Stil */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="accounting-tiles">
                <KpiTile
                    label="Seminargebühren"
                    value={formatCurrency(expectedFees)}
                    sublabel={`${filteredInvoices.length} ${filteredInvoices.length === 1 ? 'Buchung' : 'Buchungen'}`}
                    icon={Receipt}
                    tone="emerald"
                    testId="kpi-fees"
                />
                <KpiTile
                    label="Eingegangen"
                    value={formatCurrency(totalIncome)}
                    sublabel={`${paidCount} bezahlt${totalOpen > 0 ? ` · ${formatCurrency(totalOpen)} offen` : ''}${refundsPaid > 0 ? ` · −${formatCurrency(refundsPaid)} erstattet` : ''}`}
                    icon={CheckCircle}
                    tone="sky"
                    testId="kpi-incoming"
                />
                <KpiTile
                    label="Ausgaben"
                    value={formatCurrency(totalExpenses)}
                    sublabel={`${filteredExpenses.length} ${filteredExpenses.length === 1 ? 'Posten' : 'Posten'}`}
                    icon={TrendingDown}
                    tone="rose"
                    testId="kpi-expenses"
                />
                <KpiTile
                    label="Bilanz (aktuell)"
                    value={formatCurrency(balance)}
                    sublabel={balance >= 0 ? `verfügbar: ${formatCurrency(balance)}` : `Defizit: ${formatCurrency(Math.abs(balance))}`}
                    icon={Euro}
                    tone={balance >= 0 ? 'emerald' : 'rose'}
                    testId="kpi-balance"
                />
            </div>

            {/* Banner: Offene Erstattungen (Mitglieder-Guthaben aus genehmigten Stornierungen) */}
            {refundsOpenCount > 0 && (
                <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="refunds-open-banner">
                    <ArrowDownRight className="h-5 w-5 text-amber-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">
                            {refundsOpenCount} offene {refundsOpenCount === 1 ? 'Erstattung' : 'Erstattungen'} · {formatCurrency(refundsOpen)} Mitglieder-Guthaben
                        </p>
                        <p className="text-xs text-amber-800">
                            Bitte überweisen und anschließend in der Einnahmen-Liste „Erstattung als bezahlt markieren". Die Beträge reduzieren automatisch den Eingangs-Saldo.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setActiveTab('income'); setStatusFilter('refund'); }} data-testid="show-open-refunds-btn">
                        Anzeigen
                    </Button>
                </div>
            )}

            {/* Jahresvergleich-Wrapper wird unten gerendert (gelegentliche Auswertung) */}

            {/* Filters */}
            <div className="mb-4 flex items-center gap-3 flex-wrap">
                <div className="w-32">
                    <Select value={yearFilter} onValueChange={setYearFilter}>
                        <SelectTrigger data-testid="invoice-year-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Jahre</SelectItem>
                            {availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-72">
                    <Select value={seminarFilter} onValueChange={setSeminarFilter}>
                        <SelectTrigger data-testid="invoice-seminar-filter"><SelectValue placeholder="Alle Seminare" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{yearFilter === 'all' ? 'Alle Seminare (alle Jahre)' : `Alle Seminare ${yearFilter}`}</SelectItem>
                            {seminarsInYear.length === 0 && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">Keine Seminare in {yearFilter}</div>
                            )}
                            {seminarsInYear.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                    {s.course_number ? `${s.course_number} · ` : ''}{s.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="relative max-w-xs flex-1 min-w-[160px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
                </div>
            </div>

            {/* Tabs + tab-spezifische Aktion (rechtsbündig) */}
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 w-full sm:w-fit">
                    {[
                        { v: 'overview', l: 'Übersicht', icon: PieChart },
                        { v: 'income', l: `Einnahmen (${filteredInvoices.length})`, icon: TrendingUp },
                        { v: 'expenses', l: `Ausgaben (${filteredExpenses.length})`, icon: TrendingDown },
                    ].map(t => {
                        const I = t.icon;
                        return (
                            <button
                                key={t.v}
                                onClick={() => setActiveTab(t.v)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 flex-1 sm:flex-none justify-center ${activeTab === t.v ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                data-testid={`tab-${t.v}`}
                            >
                                <I className="h-3.5 w-3.5" />
                                {t.l}
                            </button>
                        );
                    })}
                </div>
                {activeTab === 'expenses' && (
                    <Button size="sm" onClick={() => navigate('/admin/expenses/new')} data-testid="add-expense-btn">
                        <Plus className="h-4 w-4 mr-1" /> Ausgabe hinzufügen
                    </Button>
                )}
            </div>

            {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : (
                <>
                {/* ===== ÜBERSICHT ===== */}
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-tab">
                        {/* Einnahmen-Übersicht */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base font-heading flex items-center gap-2 text-emerald-700">
                                    <ArrowUpRight className="h-4 w-4" />
                                    Einnahmen-Übersicht
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-0">
                                <OverviewRow label="Erwartet (Seminargebühren)" value={formatCurrency(expectedFees)} />
                                <OverviewRow label="Eingegangen" value={formatCurrency(totalIncome)} valueClass="text-emerald-700 font-semibold" />
                                <OverviewRow label="Noch offen" value={formatCurrency(totalOpen)} valueClass={totalOpen > 0 ? 'text-amber-700 font-semibold' : ''} last />
                                <div className="pt-4 mt-4 border-t">
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Zahlungsarten</div>
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border bg-muted/30 text-xs font-medium">
                                        <Banknote className="h-3.5 w-3.5" />
                                        Überweisung: {paidCount}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Ausgaben nach Kategorie */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base font-heading flex items-center gap-2 text-rose-700">
                                    <ArrowDownRight className="h-4 w-4" />
                                    Ausgaben nach Kategorie
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {expensesByCategory.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-6 text-center" data-testid="overview-no-expenses">
                                        Noch keine Ausgaben erfasst.
                                    </p>
                                ) : (
                                    <div>
                                        {expensesByCategory.map((c, idx) => (
                                            <OverviewRow
                                                key={c.category}
                                                label={c.label}
                                                value={
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{c.count}×</span>
                                                        <span className="font-medium">{formatCurrency(c.sum)}</span>
                                                    </span>
                                                }
                                                last={idx === expensesByCategory.length - 1}
                                            />
                                        ))}
                                        <div className="pt-3 mt-3 border-t flex items-center justify-between text-sm">
                                            <span className="font-semibold">Gesamt</span>
                                            <span className="font-bold text-rose-700">{formatCurrency(totalExpenses)}</span>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ===== EINNAHMEN ===== */}
                {activeTab === 'income' && (
                    <div className="border rounded-lg overflow-hidden bg-white">
                        {/* Status filter */}
                        <div className="flex items-center gap-1 p-2 border-b bg-muted/20">
                            {[
                                { v: 'all', l: 'Alle' },
                                { v: 'open', l: 'Offen' },
                                { v: 'partial', l: 'Teilbezahlt' },
                                { v: 'paid', l: 'Bezahlt' },
                                { v: 'refund', l: `Erstattungen${refundsOpenCount > 0 ? ` (${refundsOpenCount})` : ''}` },
                            ].map(f => (
                                <button key={f.v} onClick={() => setStatusFilter(f.v)} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${statusFilter === f.v ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`income-filter-${f.v}`}>{f.l}</button>
                            ))}
                        </div>
                        <table className="w-full text-sm">
                            <thead><tr className="border-b bg-muted/30">
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Seminar-Nr.</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Teilnehmer</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Seminar</th>
                                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Betrag</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground"></th>
                            </tr></thead>
                            <tbody>
                                {filteredInvoices.length === 0 ? (
                                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Keine Einträge</td></tr>
                                ) : filteredInvoices.map(row => {
                                    const sem = seminars.find(s => s.id === row.seminar_id);
                                    return (
                                    <React.Fragment key={row.id}>
                                    <tr className="border-b last:border-b-0 hover:bg-muted/20">
                                        <td className="px-4 py-2.5"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{sem?.course_number || '—'}</code></td>
                                        <td className="px-4 py-2.5 font-medium">
                                            {row.is_in_house_collective ? (
                                                <div data-testid={`collective-invoice-label-${row.id}`}>
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span>{row.participant_name}</span>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded" data-testid={`collective-invoice-badge-${row.id}`}>Sammelrechnung</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground font-normal">
                                                        Rechnung {row.invoice_number}{row.in_house_participant_count != null ? ` · ${row.in_house_participant_count} TN` : ''}{row.sent_at ? ` · gesendet an ${row.sent_to}` : ''}
                                                    </div>
                                                </div>
                                            ) : (row.participant_name || '—')}
                                        </td>
                                        <td className="px-4 py-2.5 text-muted-foreground text-xs">{getSeminarName(row.seminar_id)}</td>
                                        <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(row.amount)}</td>
                                        <td className="px-4 py-2.5">
                                            {row.status === 'paid' ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><CheckCircle className="h-3 w-3" /> Bezahlt {row.payment_date && <span className="text-green-600/70">({fmtDate(row.payment_date)})</span>}</span>
                                            ) : row.status === 'partial' ? (
                                                <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Teilbezahlt ({formatCurrency(row.paid_amount || 0)})</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full"><XCircle className="h-3 w-3" /> Offen</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex gap-1 justify-end">
                                                {(row.status === 'open' || row.status === 'partial') && (
                                                    <Button variant="outline" size="sm" onClick={() => { setSelectedInvoice(row); setPayDialogOpen(true); }} data-testid={`open-pay-dialog-${row.id}`}><Euro className="h-3.5 w-3.5 mr-1" /> Zahlung</Button>
                                                )}
                                                {row.is_in_house_collective && (
                                                    <CollectiveInvoiceActions invoice={row} defaultEmail={row.billing_address?.contact_email || sem?.in_house_company?.email || row.sent_to} onSent={loadData} onDeleted={loadData} />
                                                )}
                                                {row.status === 'paid' && !row.is_in_house_collective && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownloadPdf(row)}
                                                        disabled={downloading === row.id}
                                                        title="Quittung herunterladen"
                                                        data-testid={`download-receipt-${row.id}`}
                                                    >
                                                        {downloading === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                                    </Button>
                                                )}
                                                {(row.status === 'paid' || row.status === 'partial') && (row.payments || []).length > 0 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRevertPayment(row)}
                                                        title="Fehlbuchung zurücknehmen (Zahlung + Quittung entfernen)"
                                                        data-testid={`revert-payment-btn-${row.id}`}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    >
                                                        <RotateCcw className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {row.refund_amount > 0 && (
                                        <tr className={`border-b last:border-b-0 ${row.refund_status === 'paid' ? 'bg-emerald-50/40' : 'bg-amber-50/40'}`} data-testid={`refund-row-${row.id}`}>
                                            <td className="px-4 py-2 pl-8 text-xs text-muted-foreground" colSpan={2}>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <ArrowDownRight className="h-3.5 w-3.5 text-rose-600" />
                                                    <span className="font-medium">{row.refund_status === 'paid' ? 'Erstattung' : 'Erstattung offen (Guthaben)'}</span>
                                                    {row.refund_status === 'paid' && row.refund_date && (
                                                        <span className="text-muted-foreground"> · erstattet am {fmtDate(row.refund_date)}</span>
                                                    )}
                                                    {row.refund_status === 'pending' && row.refund_iban && (
                                                        <span className="text-muted-foreground/80 truncate"> · IBAN {row.refund_iban}</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-muted-foreground">Storno-Erstattung</td>
                                            <td className="px-4 py-2 text-right text-rose-700 font-semibold">−{formatCurrency(row.refund_amount)}</td>
                                            <td className="px-4 py-2">
                                                {row.refund_status === 'paid' ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`refund-paid-${row.id}`}><CheckCircle className="h-3 w-3" /> Erstattet</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full" data-testid={`refund-pending-${row.id}`}>offen</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                {row.refund_status === 'pending' && row.booking_id && (
                                                    <Button size="sm" variant="outline" onClick={() => { setRefundDialog(row); setRefundForm({ refund_date: new Date().toISOString().split('T')[0], refund_reference: '' }); }} data-testid={`mark-refund-paid-${row.id}`}>
                                                        <Banknote className="h-3.5 w-3.5 mr-1" /> Als erstattet markieren
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ===== AUSGABEN ===== */}
                {activeTab === 'expenses' && (
                    <div>
                        <div className="border rounded-lg overflow-hidden bg-white">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b bg-muted/30">
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Datum</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Kategorie</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Bezeichnung</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Empfänger</th>
                                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Betrag</th>
                                <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                                <th className="text-center px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Freigabe</th>
                                <th className="text-center px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Belege</th>
                                <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground">Aktionen</th>
                            </tr></thead>
                            <tbody>
                                {filteredExpenses.length === 0 ? (
                                    <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Keine Ausgaben</td></tr>
                                ) : filteredExpenses.map(row => {
                                    const belegeCount = (row.belege || []).length + (row.document_path ? 1 : 0);
                                    return (
                                    <tr
                                        key={row.id}
                                        className="border-b last:border-b-0 hover:bg-muted/30 cursor-pointer transition-colors"
                                        data-testid={`expense-row-${row.id}`}
                                        onClick={() => navigate(`/admin/expenses/${row.id}/edit`)}
                                        title="Zum Bearbeiten klicken"
                                    >
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{row.expense_date ? fmtDate(row.expense_date) : '—'}</td>
                                        <td className="px-4 py-2.5">
                                            <Badge variant="outline" className="text-xs">{row.category || 'Sonstiges'}</Badge>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="font-medium">{row.description}</div>
                                            {row.seminar_id && <div className="text-xs text-muted-foreground">{getSeminarName(row.seminar_id)}</div>}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs">{row.recipient || '—'}</td>
                                        <td className="px-4 py-2.5 text-right font-medium">{formatCurrency(row.amount)}</td>
                                        <td className="px-4 py-2.5">
                                            {row.paid ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle className="h-3 w-3" /> Bezahlt</span>
                                            ) : (
                                                <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Offen</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                                            <Switch
                                                checked={!!row.approved}
                                                onCheckedChange={() => handleToggleApprove(row)}
                                                data-testid={`expense-approve-${row.id}`}
                                            />
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {belegeCount === 0 ? (
                                                <span className="text-xs text-muted-foreground">—</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs">
                                                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                                    {belegeCount}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex gap-1 justify-end">
                                                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/expenses/${row.id}/edit`)} title="Bearbeiten" data-testid={`expense-edit-${row.id}`}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteExpense(row.id)} title="Löschen" data-testid={`expense-delete-${row.id}`}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
                </>
            )}

            {/* Jahresvergleich – nur ausklappbar am Seitenende, eher selten benötigt */}
            {showYearCompare && yearCompare && (
                <div className="mt-8 pt-4 border-t" data-testid="year-compare-wrapper">
                    <button
                        type="button"
                        onClick={() => setShowCompare(v => !v)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="year-compare-toggle"
                    >
                        <TrendingUp className="h-3.5 w-3.5" />
                        Jahresvergleich {yearCompare.currentYear} vs. {yearCompare.prevYear}
                        <span className="text-muted-foreground/70">{showCompare ? '▾' : '▸'}</span>
                    </button>
                    {showCompare && (
                        <Card className="mt-3" data-testid="year-compare-block">
                            <CardContent className="py-4">
                                {!yearCompare.hasPrev && (
                                    <div className="text-xs text-muted-foreground italic mb-3">Keine Vorjahresdaten vorhanden</div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <YearCompareItem label="Einnahmen" data={yearCompare.income} positiveIsGood={true} />
                                    <YearCompareItem label="Ausgaben" data={yearCompare.expenses} positiveIsGood={false} />
                                    <YearCompareItem label="Ergebnis" data={yearCompare.result} positiveIsGood={true} />
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Payment Dialog – gemeinsame Komponente, identisch zur Teilnehmer-Detailseite */}
            <PaymentDialog
                open={payDialogOpen}
                onOpenChange={setPayDialogOpen}
                invoice={selectedInvoice}
                participantName={selectedInvoice?.participant_name}
                seminarTitle={selectedInvoice ? getSeminarName(selectedInvoice.seminar_id) : ''}
                onSuccess={loadData}
            />

            {/* Erstattungs-Dialog (Storno-Rückzahlung) */}
            <Dialog open={!!refundDialog} onOpenChange={(o) => !o && setRefundDialog(null)}>
                <DialogContent className="max-w-md" data-testid="refund-dialog">
                    <DialogHeader>
                        <DialogTitle>Erstattung verbuchen</DialogTitle>
                        {refundDialog && (
                            <p className="text-xs text-muted-foreground">
                                {refundDialog.participant_name} · {getSeminarName(refundDialog.seminar_id)}
                            </p>
                        )}
                    </DialogHeader>
                    {refundDialog && (
                        <div className="space-y-4 py-2">
                            <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Bezahlte Seminargebühr:</span>
                                    <span>{formatCurrency(refundDialog.amount)}</span>
                                </div>
                                <div className="flex justify-between font-semibold border-t pt-1 mt-1 text-rose-700">
                                    <span>Zu erstatten:</span>
                                    <span>−{formatCurrency(refundDialog.refund_amount)}</span>
                                </div>
                                {refundDialog.refund_account_holder && (
                                    <div className="flex justify-between pt-1">
                                        <span className="text-muted-foreground">Kontoinhaber:</span>
                                        <span>{refundDialog.refund_account_holder}</span>
                                    </div>
                                )}
                                {refundDialog.refund_iban && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">IBAN:</span>
                                        <span className="font-mono text-xs">{refundDialog.refund_iban}</span>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label>Erstattungs-Datum *</Label>
                                <Input type="date" value={refundForm.refund_date} onChange={e => setRefundForm(f => ({ ...f, refund_date: e.target.value }))} data-testid="refund-date" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Verwendungszweck (optional)</Label>
                                <Input value={refundForm.refund_reference} onChange={e => setRefundForm(f => ({ ...f, refund_reference: e.target.value }))} placeholder="z. B. Erstattung Stornierung BGPR-2026-04" data-testid="refund-reference" />
                            </div>
                            <p className="text-xs text-muted-foreground bg-muted/40 rounded p-2">
                                Hinweis: Erstattungen werden nicht als Ausgabe verbucht – sie reduzieren den Eingangs-Saldo des betroffenen Seminars.
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRefundDialog(null)}>Abbrechen</Button>
                        <Button
                            disabled={saving || !refundForm.refund_date}
                            data-testid="refund-submit"
                            onClick={async () => {
                                if (!refundDialog?.booking_id) return;
                                setSaving(true);
                                try {
                                    await cancellationsApi.markRefundPaid(refundDialog.booking_id, refundForm);
                                    toast.success('Erstattung verbucht');
                                    setRefundDialog(null);
                                    loadData();
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Fehler beim Verbuchen');
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                            Erstattung speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Expense Dialog */}
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Ausgabe erfassen</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2"><Label>Seminar</Label>
                            <Select value={expenseForm.seminar_id || 'none'} onValueChange={v => setExpenseForm(f => ({ ...f, seminar_id: v === 'none' ? '' : v }))}>
                                <SelectTrigger><SelectValue placeholder="Kein Seminar zugeordnet" /></SelectTrigger>
                                <SelectContent><SelectItem value="none">Kein Seminar zugeordnet</SelectItem>{seminars.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2"><Label>Kategorie</Label>
                            <Select value={expenseForm.category} onValueChange={v => setExpenseForm(f => ({ ...f, category: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2"><Label>Beschreibung *</Label><Input value={expenseForm.description} onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))} placeholder="z.B. Honorar Dr. Müller" /></div>
                        <div className="space-y-2"><Label>Empfänger</Label><Input value={expenseForm.recipient} onChange={e => setExpenseForm(f => ({ ...f, recipient: e.target.value }))} placeholder="Name des Empfängers" /></div>
                        <div className="space-y-2"><Label>Betrag (EUR) *</Label><Input type="number" min="0" step="0.01" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>Abbrechen</Button>
                        <Button onClick={handleCreateExpense} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Erfassen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Expense Paid Dialog */}
            <Dialog open={!!expPaidDialog} onOpenChange={() => setExpPaidDialog(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Ausgabe als bezahlt markieren</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm"><strong>{expPaidDialog?.description}</strong> · {formatCurrency(expPaidDialog?.amount || 0)}</p>
                        <div className="space-y-2"><Label>Bezahlt am *</Label><Input type="date" value={expPaidDate} onChange={e => setExpPaidDate(e.target.value)} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExpPaidDialog(null)}>Abbrechen</Button>
                        <Button onClick={handlePayExpense} disabled={saving || !expPaidDate}>{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />} Bezahlt</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quittungen-Resync Dialog */}
            <Dialog open={resyncDialogOpen} onOpenChange={setResyncDialogOpen}>
                <DialogContent className="max-w-2xl" data-testid="resync-receipts-dialog">
                    <DialogHeader>
                        <DialogTitle>Quittungen in Dropbox erneuern</DialogTitle>
                    </DialogHeader>
                    {(() => {
                        const paidList = invoices.filter(i => i.status === 'paid');
                        const q = resyncSearch.trim().toLowerCase();
                        const visible = q
                            ? paidList.filter(i =>
                                (i.participant_name || '').toLowerCase().includes(q)
                                || (i.receipt_number || '').toLowerCase().includes(q)
                                || (i.invoice_number || '').toLowerCase().includes(q)
                                || getSeminarName(i.seminar_id).toLowerCase().includes(q))
                            : paidList;
                        const allVisibleSelected = visible.length > 0 && visible.every(i => resyncSelectedIds.includes(i.id));
                        const toggleAllVisible = () => {
                            const visibleIds = visible.map(i => i.id);
                            if (allVisibleSelected) {
                                setResyncSelectedIds(resyncSelectedIds.filter(id => !visibleIds.includes(id)));
                            } else {
                                const set = new Set([...resyncSelectedIds, ...visibleIds]);
                                setResyncSelectedIds(Array.from(set));
                            }
                        };
                        const toggleOne = (id) => {
                            setResyncSelectedIds(resyncSelectedIds.includes(id)
                                ? resyncSelectedIds.filter(x => x !== id)
                                : [...resyncSelectedIds, id]);
                        };
                        return (
                            <div className="space-y-3 py-2">
                                <p className="text-xs text-muted-foreground">
                                    Die ausgewählten Quittungen werden mit der aktuellen Vorlage neu gerendert
                                    und in Dropbox unter demselben Pfad überschrieben. Es werden keine E-Mails verschickt.
                                </p>
                                <div className="flex items-center gap-2">
                                    <Search className="h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Suche: Teilnehmer, Quittungs-Nr., Seminar…"
                                        value={resyncSearch}
                                        onChange={e => setResyncSearch(e.target.value)}
                                        data-testid="resync-receipts-search"
                                    />
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <Checkbox
                                            checked={allVisibleSelected}
                                            onCheckedChange={toggleAllVisible}
                                            data-testid="resync-select-all"
                                        />
                                        <span>{allVisibleSelected ? 'Auswahl entfernen' : 'Alle sichtbaren auswählen'}</span>
                                    </label>
                                    <span data-testid="resync-selection-count">
                                        {resyncSelectedIds.length} / {paidList.length} ausgewählt
                                    </span>
                                </div>
                                <div className="border rounded max-h-80 overflow-y-auto divide-y">
                                    {visible.length === 0 ? (
                                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                                            Keine bezahlten Quittungen gefunden.
                                        </div>
                                    ) : visible.map(inv => {
                                        const checked = resyncSelectedIds.includes(inv.id);
                                        const docNo = inv.receipt_number || inv.invoice_number || inv.id.slice(0, 8);
                                        return (
                                            <label
                                                key={inv.id}
                                                className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer"
                                                data-testid={`resync-row-${inv.id}`}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => toggleOne(inv.id)}
                                                    data-testid={`resync-check-${inv.id}`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">
                                                        {inv.participant_name || '—'}
                                                        <span className="ml-2 text-xs text-muted-foreground font-mono">{docNo}</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground truncate">
                                                        {getSeminarName(inv.seminar_id)}
                                                        {inv.payment_date ? ` · ${fmtDate(inv.payment_date)}` : ''}
                                                    </div>
                                                </div>
                                                <div className="text-sm font-medium tabular-nums">
                                                    {formatCurrency(inv.amount)}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResyncDialogOpen(false)} data-testid="resync-cancel-btn">
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleResyncReceipts}
                            disabled={resyncing || resyncSelectedIds.length === 0}
                            data-testid="resync-confirm-btn"
                        >
                            {resyncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            {resyncSelectedIds.length} Quittung{resyncSelectedIds.length === 1 ? '' : 'en'} erneuern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}

function SummaryCard({ icon: Icon, color, value, label }) {
    const c = { amber: 'bg-amber-100 text-amber-600', green: 'bg-green-100 text-green-600', red: 'bg-red-100 text-red-600', blue: 'bg-blue-100 text-blue-600' };
    return <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className={`p-2 rounded-lg ${c[color]}`}><Icon className="h-5 w-5" /></div><div><p className="text-xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div></div></CardContent></Card>;
}

const KPI_TONES = {
    emerald: {
        bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/60',
        ring: 'ring-emerald-200/60',
        labelColor: 'text-emerald-700',
        valueColor: 'text-emerald-900',
        sublabelColor: 'text-emerald-700/80',
        iconBg: 'bg-emerald-200/70 text-emerald-700',
    },
    sky: {
        bg: 'bg-gradient-to-br from-sky-50 to-sky-100/60',
        ring: 'ring-sky-200/60',
        labelColor: 'text-sky-700',
        valueColor: 'text-sky-900',
        sublabelColor: 'text-sky-700/80',
        iconBg: 'bg-sky-200/70 text-sky-700',
    },
    rose: {
        bg: 'bg-gradient-to-br from-rose-50 to-rose-100/60',
        ring: 'ring-rose-200/60',
        labelColor: 'text-rose-700',
        valueColor: 'text-rose-900',
        sublabelColor: 'text-rose-700/80',
        iconBg: 'bg-rose-200/70 text-rose-700',
    },
};

function KpiTile({ label, value, sublabel, icon: Icon, tone = 'emerald', testId }) {
    const t = KPI_TONES[tone] || KPI_TONES.emerald;
    return (
        <div
            className={`rounded-xl ${t.bg} ring-1 ${t.ring} p-4 sm:p-5 transition-shadow hover:shadow-md`}
            data-testid={testId}
        >
            <div className="flex items-start justify-between gap-2">
                <div className={`text-xs font-medium uppercase tracking-wide ${t.labelColor}`}>{label}</div>
                <div className={`h-8 w-8 rounded-full ${t.iconBg} flex items-center justify-center shrink-0`}>
                    <Icon className="h-4 w-4" />
                </div>
            </div>
            <div className={`text-2xl font-bold mt-2 ${t.valueColor}`}>{value}</div>
            {sublabel && <div className={`text-xs mt-1 ${t.sublabelColor}`}>{sublabel}</div>}
        </div>
    );
}

function OverviewRow({ label, value, valueClass = '', last = false }) {
    return (
        <div className={`flex items-center justify-between py-2.5 text-sm ${!last ? 'border-b border-muted/50' : ''}`}>
            <span className="text-muted-foreground">{label}</span>
            <span className={valueClass}>{value}</span>
        </div>
    );
}

function YearCompareItem({ label, data, positiveIsGood }) {
    const fmt = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);
    const pct = data.pct;
    const diff = (data.cur || 0) - (data.prev || 0);
    let trendIcon = Minus;
    let trendColor = 'text-muted-foreground';
    if (pct !== null && Math.abs(pct) > 0.1) {
        const isUp = diff > 0;
        trendIcon = isUp ? TrendingUp : TrendingDown;
        const isGood = positiveIsGood ? isUp : !isUp;
        trendColor = isGood ? 'text-emerald-600' : 'text-red-600';
    }
    const TrendIcon = trendIcon;
    return (
        <div className="space-y-1" data-testid={`year-compare-${label.toLowerCase()}`}>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg font-bold">{fmt(data.cur)}</span>
                <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
                    <TrendIcon className="h-3 w-3" />
                    {pct === null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)} %`}
                </span>
            </div>
            <div className="text-xs text-muted-foreground">
                Vorjahr: {fmt(data.prev)}
            </div>
        </div>
    );
}
